import React, { useState } from 'react';
import Modal from '../../components/Modal';
import * as gamesApi from '../../api/games';
import * as miniLeaguesApi from '../../api/miniLeagues';

/**
 * A code is six characters and gives nothing away about what it opens, so this
 * box resolves it before joining anything.
 *
 * Three things can come back, and each gets its own question:
 *  - a league code, typed in here by mistake. It used to die as "Invalid game
 *    code", which is both wrong and a dead end. Offer the league instead.
 *  - a game that sits under a league. Joining is a seat in that one game and
 *    NOT membership, which is the accident worth naming out loud - the player
 *    thinks they have joined the league and has not.
 *  - a plain standalone game, which just joins.
 */
/**
 * Whether what `joinGame` answered is a request to the manager rather than a seat. The server
 * says 202 for that (backlog item 49: the game has just rolled over, and whether a newcomer is
 * fair is the manager's call), but `request()` hands back the body alone, and only a request
 * carries the rollover it belongs to.
 */
const isJoinRequest = (answer) =>
  Boolean(answer) && answer.rolloverWeek !== undefined && answer.requestedAt !== undefined;

function JoinGameModal({ onClose, onJoined, initialCode = '' }) {
  // `initialCode` is the code somebody typed on the register form before they had an
  // account. It is only ever a starting value: the code has not been checked at that
  // point - it cannot be, since resolving one needs a session - so it arrives here
  // exactly as typed and goes through the same resolve step as anything else.
  const [joinCode, setJoinCode] = useState(initialCode);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [pending, setPending] = useState(null);
  const [requested, setRequested] = useState(null);
  const [gameRequested, setGameRequested] = useState(false);

  const afterJoiningTheGame = (answer) => {
    if (isJoinRequest(answer)) {
      setGameRequested(true);
    } else {
      onJoined();
    }
  };

  const failure = (err, fallback) => {
    if (err.name === 'ApiError') {
      setError(err.body?.error || fallback);
    } else {
      setError(fallback);
    }
  };

  // Step one: find out what the code is before committing the player to it.
  const handleResolve = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const found = await gamesApi.resolveJoinCode(joinCode);

      if (found.kind === 'UNKNOWN') {
        setError("That code doesn't match any game or mini-league.");
        return;
      }
      if (found.kind === 'GAME' && !found.leagueId) {
        afterJoiningTheGame(await gamesApi.joinGame(joinCode));
        return;
      }
      setPending(found);
    } catch (err) {
      failure(err, 'Error checking that code');
    } finally {
      setSubmitting(false);
    }
  };

  const joinTheGame = async () => {
    setError('');
    setSubmitting(true);
    try {
      afterJoiningTheGame(await gamesApi.joinGame(joinCode));
    } catch (err) {
      failure(err, 'Failed to join game');
    } finally {
      setSubmitting(false);
    }
  };

  const joinTheLeague = async () => {
    setError('');
    setSubmitting(true);
    try {
      const result = await miniLeaguesApi.joinLeague(joinCode);
      if (result?.outcome === 'REQUESTED') {
        setRequested(result);
      } else {
        onJoined();
      }
    } catch (err) {
      failure(err, 'Failed to join league');
    } finally {
      setSubmitting(false);
    }
  };

  // A game that has just rolled over: everybody is back in, and whether a newcomer joins now is
  // the manager's call, so the code raised a request. Asking is a success, not a failure.
  if (gameRequested) {
    return (
      <Modal title="Request sent" onClose={onClose}>
        <div className="join-requested">
          <p>
            That game has just rolled over, so your request to join has gone to the manager.
          </p>
          <p className="helper-text">
            If they let you in before the next round's deadline, you start level with everybody
            else. Nothing else to do.
          </p>
          <button type="button" className="primary-btn full-width" onClick={onClose}>
            Done
          </button>
        </div>
      </Modal>
    );
  }

  // Same ending as the league screen's own join: asking is a success, not a failure.
  if (requested) {
    return (
      <Modal title="Request sent" onClose={onClose}>
        <div className="join-requested">
          <p>
            <strong>{requested.leagueName}</strong> has already started, so your
            request has gone to the manager.
          </p>
          <p className="helper-text">
            {requested.vacant
              ? "This league doesn't have a manager right now. Your request will be waiting for whoever takes it on."
              : "You'll be in as soon as they say yes. Nothing else to do."}
          </p>
          <button type="button" className="primary-btn full-width" onClick={onClose}>
            Done
          </button>
        </div>
      </Modal>
    );
  }

  if (pending?.kind === 'LEAGUE') {
    return (
      <Modal title="That's a mini-league code" onClose={onClose}>
        <div className="join-choice">
          <p>
            <strong>{joinCode}</strong> is the code for the mini-league{' '}
            <strong>{pending.leagueName}</strong>, not a single game.
          </p>
          <p className="helper-text">
            {pending.joinOpen
              ? 'Joining enters you into every tournament it runs, now and in future.'
              : 'It has already started, so joining sends a request for the manager to approve.'}
          </p>
          {error && <div className="error-message">{error}</div>}
          <button
            type="button"
            className="primary-btn full-width"
            onClick={joinTheLeague}
            disabled={submitting}
          >
            {submitting
              ? 'Joining...'
              : pending.joinOpen
                ? `Join ${pending.leagueName}`
                : `Ask to join ${pending.leagueName}`}
          </button>
          <button
            type="button"
            className="secondary-btn full-width"
            onClick={() => setPending(null)}
            disabled={submitting}
          >
            Use a different code
          </button>
        </div>
      </Modal>
    );
  }

  if (pending?.kind === 'GAME') {
    return (
      <Modal title="This game belongs to a mini-league" onClose={onClose}>
        <div className="join-choice">
          <p>
            <strong>{pending.gameName}</strong> is run by the mini-league{' '}
            <strong>{pending.leagueName}</strong>.
          </p>
          <p className="helper-text">
            Joining it makes you a member of {pending.leagueName}, so you'll be
            entered into its other tournaments and its next season too.
          </p>
          {error && <div className="error-message">{error}</div>}
          <button
            type="button"
            className="primary-btn full-width"
            onClick={joinTheGame}
            disabled={submitting}
          >
            {submitting ? 'Joining...' : `Join ${pending.leagueName}`}
          </button>
          <button
            type="button"
            className="secondary-btn full-width"
            onClick={() => setPending(null)}
            disabled={submitting}
          >
            Use a different code
          </button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal title="Join Game" onClose={onClose}>
      <form onSubmit={handleResolve}>
        <div className="form-group">
          <label>Game Code</label>
          <input
            type="text"
            value={joinCode}
            onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
            placeholder="Enter 6-character code"
            maxLength={8}
            autoFocus
            required
            style={{ textTransform: 'uppercase', letterSpacing: '2px', textAlign: 'center', fontSize: '1.2rem' }}
          />
          <p className="helper-text">
            A mini-league code works here too - we'll ask which you meant.
          </p>
        </div>
        {error && <div className="error-message">{error}</div>}
        <button type="submit" className="primary-btn full-width" disabled={submitting}>
          {submitting ? 'Checking...' : 'Continue'}
        </button>
      </form>
    </Modal>
  );
}

export default JoinGameModal;
